import { useState } from 'react';

type Season = 'spring' | 'summer' | 'fall' | 'winter';

const seasons: { id: Season; label: string; icon: string; color: string }[] = [
  { id: 'spring', label: 'Spring', icon: 'ri-seedling-line', color: '#4A9B6F' },
  { id: 'summer', label: 'Summer', icon: 'ri-sun-line', color: '#C8A97E' },
  { id: 'fall', label: 'Fall', icon: 'ri-leaf-line', color: '#B5722A' },
  { id: 'winter', label: 'Winter', icon: 'ri-snowy-line', color: '#6B9EC4' },
];

const seasonData: Record<
  Season,
  { title: string; description: string; services: { icon: string; label: string; note: string }[] }
> = {
  spring: {
    title: 'Spring Awakening',
    description: 'We kick the season off right — clearing out winter debris, feeding the lawn, and getting your property looking its best before summer.',
    services: [
      { icon: 'ri-broom-line', label: 'Spring Cleanup', note: 'Debris, dead leaves & branch removal' },
      { icon: 'ri-water-percent-line', label: 'Aeration & Overseeding', note: 'Thicken the lawn after winter' },
      { icon: 'ri-flask-line', label: 'Starter Fertilization', note: 'Nutrient boost for new growth' },
      { icon: 'ri-plant-line', label: 'Mulching', note: 'Fresh mulch to garden beds' },
      { icon: 'ri-scissors-cut-line', label: 'Shrub Pruning', note: 'Shape and remove dead growth' },
      { icon: 'ri-home-gear-line', label: 'Gutter Cleaning', note: 'Clear winter buildup (Premium)' },
    ],
  },
  summer: {
    title: 'Summer Maintenance',
    description: "Consistent weekly care keeps your lawn lush and healthy through the heat. We handle it so you don't have to.",
    services: [
      { icon: 'ri-landscape-line', label: 'Weekly Mowing', note: 'Cut to ideal height for the season' },
      { icon: 'ri-ruler-line', label: 'Edging & Trimming', note: 'Crisp lines along all borders' },
      { icon: 'ri-bug-line', label: 'Weed Control', note: 'Garden bed weeding & maintenance' },
      { icon: 'ri-leaf-line', label: 'Hedge Trimming', note: 'Monthly shaping & cleanup' },
      { icon: 'ri-flask-line', label: 'Summer Fertilization', note: 'Slow-release summer feed' },
      { icon: 'ri-eye-line', label: 'Property Inspection', note: 'Spot issues before they grow' },
    ],
  },
  fall: {
    title: 'Fall Preparation',
    description: "We prep your property to handle the Ontario winter — cleanup, winterizer, and protection so spring starts on the right foot.",
    services: [
      { icon: 'ri-leaf-line', label: 'Leaf Removal', note: 'Full property leaf cleanup' },
      { icon: 'ri-landscape-line', label: 'Final Mowing', note: 'Cut to winter-ready height' },
      { icon: 'ri-flask-line', label: 'Winterizer Fertilization', note: 'Feed roots for spring rebound' },
      { icon: 'ri-scissors-cut-line', label: 'Shrub Wrapping', note: 'Winter protection for sensitive plants' },
      { icon: 'ri-plant-line', label: 'Garden Bed Prep', note: 'Clear, edge and cover for winter' },
      { icon: 'ri-home-gear-line', label: 'Gutter Cleaning', note: 'Pre-winter gutter clearout (Premium)' },
    ],
  },
  winter: {
    title: 'Winter Snow Services',
    description: 'Available with our Premium package — reliable snow removal after every storm so your driveway and walkways are always clear and safe.',
    services: [
      { icon: 'ri-truck-line', label: 'Snow Plowing', note: 'Driveways cleared after every storm' },
      { icon: 'ri-footprint-line', label: 'Walkway Shovelling', note: 'All paths and steps cleared' },
      { icon: 'ri-water-flash-line', label: 'De-icing / Salting', note: 'Prevent ice buildup on surfaces' },
      { icon: 'ri-shield-check-line', label: 'Storm Response', note: 'Priority service after major snowfall' },
      { icon: 'ri-building-line', label: 'Property Monitoring', note: 'We check in during extended cold snaps' },
      { icon: 'ri-phone-line', label: 'On-call Support', note: 'Reach us anytime during storm season' },
    ],
  },
};

export default function SeasonalServices() {
  const [active, setActive] = useState<Season>('spring');
  const data = seasonData[active];
  const seasonMeta = seasons.find((s) => s.id === active)!;

  return (
    <section className="bg-[#F7F5F1] py-20 px-6">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <p className="text-[#C8A97E] text-xs font-bold tracking-[0.4em] uppercase mb-4">YEAR-ROUND CARE</p>
          <h2
            className="text-[#2B5741] text-4xl font-bold mb-3"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Seasonal Services
          </h2>
          <p className="text-gray-500 text-sm max-w-md mx-auto">
            Every season brings different needs. Here's exactly what we take care of throughout the year.
          </p>
        </div>

        {/* Season tab strip */}
        <div className="flex rounded-xl overflow-hidden border border-gray-200 mb-8 bg-white">
          {seasons.map((s) => (
            <button
              key={s.id}
              onClick={() => setActive(s.id)}
              className={`flex-1 flex flex-col items-center gap-1.5 py-4 px-2 text-xs font-bold uppercase tracking-wider transition-all cursor-pointer ${
                active === s.id
                  ? 'bg-[#2B5741] text-white'
                  : 'text-gray-500 hover:bg-gray-50'
              }`}
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${s.icon} text-base`}></i>
              </div>
              <span className="hidden sm:block">{s.label}</span>
            </button>
          ))}
        </div>

        {/* Content panel */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          {/* Panel header */}
          <div className="bg-[#2B5741] px-6 py-6 flex flex-col sm:flex-row sm:items-center gap-3">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: `${seasonMeta.color}30` }}
            >
              <i className={`${seasonMeta.icon} text-xl`} style={{ color: seasonMeta.color }}></i>
            </div>
            <div>
              <h3
                className="text-white text-xl font-bold"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                {data.title}
              </h3>
              <p className="text-white/65 text-xs mt-0.5 leading-relaxed max-w-xl">
                {data.description}
              </p>
            </div>
          </div>

          {/* Service grid */}
          <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.services.map((svc) => (
              <div key={svc.label} className="flex items-start gap-3 p-4 rounded-lg bg-[#F7F5F1]">
                <div className="w-8 h-8 flex items-center justify-center bg-[#2B5741] rounded-md flex-shrink-0">
                  <i className={`${svc.icon} text-white text-sm`}></i>
                </div>
                <div>
                  <p className="text-gray-800 text-sm font-semibold">{svc.label}</p>
                  <p className="text-gray-400 text-xs mt-0.5">{svc.note}</p>
                </div>
              </div>
            ))}
          </div>

          {active === 'winter' && (
            <div className="px-6 pb-5">
              <div className="bg-[#2B5741]/8 border border-[#2B5741]/20 rounded-lg p-4 flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-information-line text-[#2B5741]"></i>
                </div>
                <p className="text-xs text-gray-600">
                  <strong className="text-[#2B5741]">Premium package only.</strong> Snow removal is included year-round in our Premium plan. Standard and Basic packages can add snow service as an optional add-on — call us to discuss.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
